import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CSpinner } from '@coreui/react';
import api from '../services/api';
import { useNotifications } from '../context/NotificationContext';

const _apiUrl = process.env.REACT_APP_API_URL || '';
const MEDIA_BASE = _apiUrl.endsWith('/api')
  ? _apiUrl.slice(0, -4)
  : (typeof window !== 'undefined' ? window.location.origin : '');

function resizeImageClient(file, maxPx = 500) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width >= height) { height = Math.round(height * maxPx / width); width = maxPx; }
        else { width = Math.round(width * maxPx / height); height = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', 0.82);
    };
    img.onerror = reject;
    img.src = url;
  });
}

function fmtTime(dt) {
  return new Date(dt).toLocaleString('en-MY', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

function StarRating({ value }) {
  if (!value) return null;
  return (
    <span style={{ fontSize: 12, color: '#f1c40f' }}>
      {'★'.repeat(value)}{'☆'.repeat(5 - value)}
    </span>
  );
}

// ── Chat window for one session ───────────────────────────────────────────────
function ChatWindow({ session, onClose, onSessionUpdate }) {
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState('');
  const [sending, setSending]       = useState(false);
  const [ending, setEnding]         = useState(false);
  const [imgPreview, setImgPreview] = useState(null);
  const [imgFile, setImgFile]       = useState(null);
  const bottomRef = useRef(null);
  const fileRef   = useRef(null);
  const { subscribe } = useNotifications();

  const sessionEnded = session.status === 'ended';

  const loadMessages = useCallback(async () => {
    try {
      const { data } = await api.get(`/chat/session/${session.session_id}`);
      setMessages(data.messages || []);
      if (data.session && data.session.status !== session.status) {
        onSessionUpdate(session.session_id, data.session);
      }
    } catch {}
  }, [session.session_id, session.status, onSessionUpdate]);

  useEffect(() => {
    loadMessages();
    const unsub = subscribe(msg => {
      if (
        (msg.type === 'chat_message' || msg.type === 'chat_reply') &&
        msg.customer_uid === session.customer_uid &&
        msg.session_id === session.session_id
      ) {
        loadMessages();
      }
      if (msg.type === 'chat_session_ended' && msg.session_id === session.session_id) {
        onSessionUpdate(session.session_id, { status: 'ended' });
      }
    });
    return unsub;
  }, [loadMessages, session.customer_uid, session.session_id, subscribe, onSessionUpdate]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    try {
      const blob = await resizeImageClient(file);
      setImgFile(new File([blob], 'image.jpg', { type: 'image/jpeg' }));
      setImgPreview(URL.createObjectURL(blob));
    } catch {
      setImgFile(file);
      setImgPreview(URL.createObjectURL(file));
    }
  }

  function clearImage() {
    setImgFile(null);
    if (imgPreview) URL.revokeObjectURL(imgPreview);
    setImgPreview(null);
  }

  async function handleSend(e) {
    e.preventDefault();
    if (sending || sessionEnded) return;
    if (imgFile) {
      setSending(true);
      try {
        const form = new FormData();
        form.append('image', imgFile);
        await api.post(`/chat/session/${session.session_id}/image`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        clearImage();
        loadMessages();
      } catch {}
      setSending(false);
      return;
    }
    if (!input.trim()) return;
    setSending(true);
    try {
      await api.post(`/chat/session/${session.session_id}`, { message: input.trim() });
      setInput('');
      loadMessages();
    } catch {}
    setSending(false);
  }

  async function handleEndSession() {
    if (!window.confirm('End this session?')) return;
    setEnding(true);
    try {
      await api.post(`/chat/session/${session.session_id}/end`);
      onSessionUpdate(session.session_id, { status: 'ended' });
    } catch {}
    setEnding(false);
  }

  async function handlePin() {
    const isPinned = !!session.is_pinned;
    if (!isPinned) {
      const note = window.prompt('Optional training note (leave blank to skip):', session.pin_note || '') ;
      if (note === null) return; // cancelled
      try {
        await api.post(`/chat/session/${session.session_id}/pin`, { pin: true, note });
        onSessionUpdate(session.session_id, { is_pinned: 1, pin_note: note });
      } catch {}
    } else {
      if (!window.confirm('Unpin this session? It will be deleted after the retention period.')) return;
      try {
        await api.post(`/chat/session/${session.session_id}/pin`, { pin: false });
        onSessionUpdate(session.session_id, { is_pinned: 0, pin_note: null });
      } catch {}
    }
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100%',
      background: '#fff', borderRadius: 10,
      boxShadow: '0 2px 16px rgba(0,0,0,0.1)', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        background: sessionEnded
          ? 'linear-gradient(135deg, #555, #777)'
          : 'linear-gradient(135deg, #071e2b, #0d3a4a)',
        padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ color: '#fff', fontWeight: 700, fontSize: 15 }}>{session.nama}</span>
            {session.is_pinned ? (
              <span style={{ fontSize: 11, background: '#f1c40f', color: '#333', borderRadius: 4, padding: '1px 6px', fontWeight: 700 }}>📌 Pinned</span>
            ) : null}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginTop: 2 }}>
            {session.telhp}
            {session.rating ? <span style={{ marginLeft: 8 }}><StarRating value={session.rating} /></span> : null}
            {sessionEnded && <span style={{ marginLeft: 6, fontSize: 11, background: 'rgba(255,255,255,0.15)', borderRadius: 4, padding: '1px 5px' }}>Ended</span>}
          </div>
          {session.pin_note && <div style={{ color: '#f1c40f', fontSize: 11, marginTop: 3, fontStyle: 'italic' }}>{session.pin_note}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button onClick={handlePin} title={session.is_pinned ? 'Unpin session' : 'Pin for training'}
            style={{ background: session.is_pinned ? '#f1c40f' : 'rgba(255,255,255,0.15)', border: 'none', color: session.is_pinned ? '#333' : '#fff', borderRadius: 6, padding: '5px 8px', fontSize: 13, cursor: 'pointer' }}>
            📌
          </button>
          {!sessionEnded && (
            <button onClick={handleEndSession} disabled={ending}
              style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
              {ending ? '…' : 'End Session'}
            </button>
          )}
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: '#aaa', fontSize: 13, marginTop: 24 }}>No messages yet.</div>
        )}
        {messages.map(m => (
          <div key={m.id} style={{ alignSelf: m.sender === 'staff' ? 'flex-end' : 'flex-start', maxWidth: '78%' }}>
            {m.msg_type === 'image' && m.image_url ? (
              <a href={`${MEDIA_BASE}${m.image_url}`} target="_blank" rel="noreferrer">
                <img src={`${MEDIA_BASE}${m.image_url}`} alt="attachment" style={{ maxWidth: 220, borderRadius: 8, display: 'block' }} />
              </a>
            ) : (
              <div style={{
                background: m.sender === 'staff' ? '#8bc34a' : '#f0f0f0',
                color: m.sender === 'staff' ? '#fff' : '#222',
                borderRadius: m.sender === 'staff' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                padding: '8px 12px', fontSize: 13, lineHeight: 1.45,
              }}>{m.message}</div>
            )}
            <div style={{ fontSize: 10, color: '#bbb', marginTop: 2, textAlign: m.sender === 'staff' ? 'right' : 'left' }}>
              {m.sender_name} · {fmtTime(m.created_at)}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {sessionEnded && (
        <div style={{ padding: '10px 14px', borderTop: '1px solid #f0f0f0', textAlign: 'center', color: '#aaa', fontSize: 12 }}>
          Session ended{session.ended_at ? ` · ${fmtTime(session.ended_at)}` : ''}
        </div>
      )}

      {/* Image preview */}
      {!sessionEnded && imgPreview && (
        <div style={{ padding: '8px 14px', borderTop: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <img src={imgPreview} alt="preview" style={{ height: 60, borderRadius: 6, objectFit: 'cover' }} />
          <button onClick={clearImage} style={{ background: 'none', border: 'none', color: '#c0392b', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
      )}

      {/* Input */}
      {!sessionEnded && (
        <form onSubmit={handleSend} style={{ display: 'flex', gap: 8, padding: '10px 14px', borderTop: '1px solid #f0f0f0', alignItems: 'center' }}>
          <button type="button" onClick={() => fileRef.current?.click()}
            style={{ background: 'none', border: '1px solid #dde1e7', borderRadius: 8, padding: '7px 10px', cursor: 'pointer', color: '#6c757d', display: 'flex', alignItems: 'center' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
          </button>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
          <input
            value={input} onChange={e => setInput(e.target.value)}
            placeholder={imgFile ? 'Click → to send image' : 'Type a reply…'}
            disabled={!!imgFile}
            style={{ flex: 1, padding: '8px 12px', border: '1px solid #dde1e7', borderRadius: 8, fontSize: 13, outline: 'none' }}
          />
          <button type="submit" disabled={sending || (!input.trim() && !imgFile)}
            style={{ padding: '8px 14px', background: '#8bc34a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 700 }}>
            {sending ? <CSpinner size="sm" style={{ color: '#fff' }} /> : '→'}
          </button>
        </form>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HelpDeskPage() {
  const [sessions, setSessions]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeId, setActiveId]       = useState(null); // session_id
  const [localUnread, setLocalUnread] = useState({});
  const { subscribe, clearChatUnread } = useNotifications();

  useEffect(() => { clearChatUnread(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get('/chat/sessions');
      setSessions(data.sessions || []);
      const map = {};
      (data.sessions || []).forEach(s => { if (s.unread > 0) map[s.session_id] = Number(s.unread); });
      setLocalUnread(prev => ({ ...map, ...prev }));
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSessions();
    const interval = setInterval(loadSessions, 15000);
    const unsub = subscribe(msg => {
      if (msg.type === 'chat_message') {
        setLocalUnread(prev => ({ ...prev, [msg.session_id]: (prev[msg.session_id] || 0) + 1 }));
        loadSessions();
      }
      if (msg.type === 'chat_session_ended') {
        setSessions(prev => prev.map(s =>
          s.session_id === msg.session_id ? { ...s, status: 'ended' } : s
        ));
      }
    });
    return () => { clearInterval(interval); unsub(); };
  }, [loadSessions, subscribe]);

  function openSession(sid) {
    setActiveId(sid);
    setLocalUnread(prev => ({ ...prev, [sid]: 0 }));
  }

  const handleSessionUpdate = useCallback((sid, patch) => {
    setSessions(prev => prev.map(s => s.session_id === sid ? { ...s, ...patch } : s));
  }, []);

  const activeSession = sessions.find(s => s.session_id === activeId);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 60 }}>
        <CSpinner style={{ color: '#8bc34a' }} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 16, height: 'calc(100vh - 160px)', minHeight: 400 }}>
      {/* Session list */}
      <div style={{
        width: 280, flexShrink: 0, background: '#fff', borderRadius: 10,
        boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflowY: 'auto',
      }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: 700, fontSize: 14, color: '#0d3a4a' }}>
          Helpdesk Sessions
        </div>
        {sessions.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: '#aaa', fontSize: 13 }}>No sessions yet.</div>
        )}
        {sessions.map(s => {
          const unread   = localUnread[s.session_id] || 0;
          const isActive = activeId === s.session_id;
          const ended    = s.status === 'ended';
          return (
            <div key={s.session_id}
              onClick={() => openSession(s.session_id)}
              style={{
                padding: '11px 16px', cursor: 'pointer',
                background: isActive ? '#f0f9e8' : 'transparent',
                borderLeft: isActive ? '3px solid #8bc34a' : '3px solid transparent',
                borderBottom: '1px solid #f8f8f8',
                display: 'flex', alignItems: 'center', gap: 10,
                opacity: ended ? 0.6 : 1,
              }}
              onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#fafafa'; }}
              onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = ended ? 'transparent' : 'transparent'; }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ fontWeight: unread > 0 ? 700 : 500, fontSize: 13, color: '#0d3a4a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.nama}
                  </span>
                  {s.is_pinned ? <span style={{ fontSize: 10, color: '#b8860b', flexShrink: 0 }}>📌</span> : null}
                  {ended && !s.is_pinned && <span style={{ fontSize: 10, color: '#aaa', flexShrink: 0 }}>ended</span>}
                </div>
                <div style={{ fontSize: 11, color: '#aaa', marginTop: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
                  {fmtTime(s.last_message_at)}
                  {s.rating ? <StarRating value={s.rating} /> : null}
                </div>
              </div>
              {unread > 0 && (
                <span style={{
                  background: '#e74a3b', color: '#fff', borderRadius: '50%',
                  width: 20, height: 20, fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Chat window */}
      <div style={{ flex: 1 }}>
        {activeSession ? (
          <ChatWindow
            key={activeId}
            session={activeSession}
            onClose={() => setActiveId(null)}
            onSessionUpdate={handleSessionUpdate}
          />
        ) : (
          <div style={{
            height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: '#fff', borderRadius: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
            color: '#aaa', fontSize: 14,
          }}>
            Select a session to start chatting
          </div>
        )}
      </div>
    </div>
  );
}
